import { authHeadersFor } from '../../core/impersonation';
import { authSnapshot, type AuthSnapshot } from '../ProgressReports/students';
import type { Granularity, ReportWindow } from '../ImplementationReport/api';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type LibraryFile = { url: string; fileName: string; fileType: string };

export type LibraryAssignment = {
  id: string;
  title: string;
  startAt: string;
  closeAt: string;
  teacherUserId: string;
  teacherFirstName: string;
  teacherLastName: string;
  schoolIds: string[];
  grades: string[];
  templateType: string;
  speakingPrompt: string;
  writingPrompt: string;
  separateWritingPrompt: boolean;
  image?: LibraryFile;
  speakingFile?: LibraryFile;
  writingFile?: LibraryFile;
  assigned: number;
  submitted: number;
  scored: number;
};

export type LibrarySchool = { id: string; name: string };

export type LibraryCaseload = { teacherUserId: string; schoolId: string; students: number };

export type LibraryReport = {
  periodStart: string;
  periodEnd: string;
  timeZone: string;
  assignments: LibraryAssignment[];
  schools: LibrarySchool[];
  caseloads: LibraryCaseload[];
};

export type AssignmentLibraryResponse = {
  available: boolean;
  granularity?: Granularity;
  today?: string;
  districtName?: string;
  period?: ReportWindow;
  report?: LibraryReport | null;
};

export type LibraryQuery = { granularity: Granularity; start?: string; today?: string; tz?: string; assignment?: string };

export type LibrarySource = (auth: AuthSnapshot, query: LibraryQuery) => Promise<AssignmentLibraryResponse>;

export class AssignmentLibraryError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'AssignmentLibraryError';
    this.status = status;
  }
}

export function viewerZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

function queryString(query: LibraryQuery): string {
  const params = new URLSearchParams({ granularity: query.granularity });
  if (query.start) params.set('start', query.start);
  if (query.today) params.set('today', query.today);
  if (query.tz) params.set('tz', query.tz);
  if (query.assignment) params.set('assignment', query.assignment);
  return `?${params.toString()}`;
}

function withLists(response: AssignmentLibraryResponse): AssignmentLibraryResponse {
  const report = response.report;
  if (!report) return response;
  return {
    ...response,
    report: {
      ...report,
      assignments: (report.assignments ?? []).map((a) => ({ ...a, schoolIds: a.schoolIds ?? [], grades: a.grades ?? [] })),
      schools: report.schools ?? [],
      caseloads: report.caseloads ?? [],
    },
  };
}

export async function fetchAssignmentLibrary(auth?: AuthSnapshot, query: LibraryQuery = { granularity: 'quarter' }): Promise<AssignmentLibraryResponse> {
  const snapshot = auth ?? (await authSnapshot());
  if (snapshot.token === undefined) throw new AssignmentLibraryError('no session token', 0);
  const response = await fetch(`${apiUrl}/assignment-library${queryString(query)}`, {
    headers: authHeadersFor(snapshot.token, snapshot.target),
  });
  if (!response.ok) {
    throw new AssignmentLibraryError(`assignment library fetch failed: ${response.status}`, response.status);
  }
  return withLists((await response.json()) as AssignmentLibraryResponse);
}
